/**
 * One shared IntersectionObserver for "is this near the screen?" (thumbnails, vignette paintings). Elements inside a
 * sideways shelf are clipped by the shelf, so only cards actually scrolled into view count; vertically half a screen
 * ahead counts too. Heavy content (decoded images) is mounted while in view and released when it leaves, so a long
 * browse never accumulates hundreds of decoded pictures (the WebContent memory that iOS kills the app for).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type Cb = (inView: boolean, width: number) => void;
const cbs = new WeakMap<Element, Cb>();
let io: IntersectionObserver | null = null;
function observer(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (!io)
    io = new IntersectionObserver((es) => es.forEach((e) => cbs.get(e.target)?.(e.isIntersecting, e.boundingClientRect.width)), { rootMargin: '50% 0px 50% 0px' });
  return io;
}

/** [ref, inView, width]: inView is true when IntersectionObserver is unavailable. */
export function useInView<T extends Element>(): [(el: T | null) => void, boolean, number] {
  const [state, setState] = useState<{ v: boolean; w: number }>(() => ({ v: typeof IntersectionObserver === 'undefined', w: 0 }));
  const el = useRef<T | null>(null);
  useEffect(
    () => () => {
      if (el.current) {
        observer()?.unobserve(el.current);
        cbs.delete(el.current);
      }
    },
    [],
  );
  const ref = useCallback((node: T | null) => {
    const o = observer();
    if (el.current && el.current !== node) {
      o?.unobserve(el.current);
      cbs.delete(el.current);
    }
    el.current = node;
    if (!node || !o) return;
    cbs.set(node, (v, w) => setState((s) => (s.v === v && (s.w === w || !v) ? s : { v, w: v ? w : s.w })));
    o.observe(node);
  }, []);
  return [ref, state.v, state.w];
}
